import React, { useState, useEffect, useRef } from 'react';
import HeaderBar from '../../components/header';
import { TextField, InputAdornment, Button, CircularProgress, Snackbar, Alert, Typography, Tooltip } from '@mui/material';
import Box from '@mui/material/Box';
import SearchIcon from '@mui/icons-material/Search';
import DashboardIcon from '@mui/icons-material/Dashboard';
import FolderSpecialIcon from '@mui/icons-material/FolderSpecial';
import AddIcon from '@mui/icons-material/Add';
import FolderIcon from '@mui/icons-material/Folder';
import AssignmentIcon from '@mui/icons-material/Assignment';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { Project, ProjectMember } from '../../interfaces/project';
import ProjectCard from '../../components/projectCard';
import CreateProjectModal from '../../components/createProjectModal';
import DeleteProjectModal from '../../components/deleteProjectModal';
import EditProjectModal from '../../components/editProjectModal';
import api from '../../services/api';
import { projectService } from '../../services/project';
import { useToast } from '../../contexts/ToastContext';

import {
  HubContainer,
  Header,
  HeaderContent,
  Title,
  Subtitle,
  CreateButton,
  MainContent,
  SectionTitle,
  ProjectsGrid,
  EmptyState,
  EmptyStateIcon,
  EmptyStateText,
  EmptyStateSubtext,
  SearchContainer,
  TabsContainer,
  TabButton,
  ProjectsCountText,
} from './styles';

const HubPage: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { showError, showSuccess } = useToast();
  
  // Verificar se o usuário é administrador ou desenvolvedor do sistema
  const isAdminOrDeveloper = (
    user?.role?.toLowerCase() === 'administrator' || 
    user?.role?.toLowerCase() === 'admin' ||
    user?.role?.toLowerCase() === 'developer' ||
    user?.role?.toLowerCase() === 'dev' ||
    // Verificar se o título inclui "Administrador do Sistema"
    (typeof user?.role === 'string' && user.role.toLowerCase().includes('administrador do sistema'))
  );

  
  const [loading, setLoading] = useState(true);
  const [projects, setProjects] = useState<Project[]>([]);
  const [filteredProjects, setFilteredProjects] = useState<Project[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [activeTab, setActiveTab] = useState('all');
  const [hasScrollOverflow, setHasScrollOverflow] = useState(false);
  const projectsGridRef = useRef<HTMLDivElement>(null);
  const [createProjectModalOpen, setCreateProjectModalOpen] = useState(false);

  // Estados para o modal de exclusão
  const [deleteProjectModalOpen, setDeleteProjectModalOpen] = useState(false);
  const [projectToDelete, setProjectToDelete] = useState<{id: string, name: string} | null>(null);
  
  // Estados para o modal de edição
  const [editProjectModalOpen, setEditProjectModalOpen] = useState(false);
  const [projectToEdit, setProjectToEdit] = useState<Project | null>(null);

  const fetchProjects = async () => {
    setLoading(true);
    try {
      // Usar o serviço de projeto para buscar os projetos
      const projectsData = await projectService.getProjects();
      
      setProjects(projectsData);
      setFilteredProjects(projectsData);
    } catch (error: any) {
      console.error('Erro ao carregar projetos:', error);
      showError(`Erro ao carregar projetos: ${error.message}`);
      // Em caso de erro, inicializa com array vazio
      setProjects([]);
      setFilteredProjects([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // Verificar se há overflow na grade de projetos
    const checkOverflow = () => {
      if (projectsGridRef.current) {
        const { scrollHeight, clientHeight } = projectsGridRef.current;
        setHasScrollOverflow(scrollHeight > clientHeight);
      }
    };

    // Verificar overflow após carregar projetos
    if (!loading && filteredProjects.length > 0) {
      // Pequeno timeout para garantir que a renderização foi concluída
      setTimeout(checkOverflow, 100);
    }

    // Adicionar listener para redimensionamento da janela
    window.addEventListener('resize', checkOverflow);
    return () => window.removeEventListener('resize', checkOverflow);
  }, [loading, filteredProjects, activeTab]);

  useEffect(() => {
    fetchProjects();
  }, []);

  useEffect(() => {
    // Filtrar projetos com base no termo de pesquisa e na tab ativa
    if (!user || !projects.length) {
      setFilteredProjects([]);
      return;
    }
    
    let filtered = [...projects];
    
    // Aplicar filtro de pesquisa
    if (searchTerm) {
      filtered = filtered.filter(project => {
        const title = project.title || project.name || '';
        const description = project.description || '';
        
        return title.toLowerCase().includes(searchTerm.toLowerCase()) ||
               description.toLowerCase().includes(searchTerm.toLowerCase());
      });
    }
    
    // Aplicar filtro da tab
    if (activeTab === 'owned') {
      filtered = filtered.filter(project => {
        // ID do usuário logado
        const userId = user.id;
        
        // Verificar se o usuário é o criador
        const isCreator = project.creatorId === userId || project.ownerId === userId;
        if (isCreator) {
          return true;
        }
        
        // Verificar se está na lista de administradores
        let isAdmin = false;
        
        // Checar se administrators existe e é um array
        if (Array.isArray(project.administrators)) {
          isAdmin = project.administrators.some((admin: ProjectMember) => admin.id === userId);
        }
        
        // Checar se admins existe e é um array (campo alternativo)
        if (!isAdmin && Array.isArray(project.admins)) {
          isAdmin = project.admins.some(admin => admin.id === userId);
        }
        
        if (isAdmin) {
          return true;
        }
        
        // Verificar se está na lista de membros
        let isMember = false;
        
        if (Array.isArray(project.members)) {
          isMember = project.members.some(member => member.id === userId);
        }
        
        // Um administrador também é considerado membro do projeto
        return isMember || isAdmin;
      });
      
    } else if (activeTab === 'recent') {
      // Ordenar por data de atualização mais recente
      filtered = [...filtered].sort((a, b) => {
        const dateA = new Date(a.updatedAt || a.lastUpdatedAt || a.createdAt || 0).getTime();
        const dateB = new Date(b.updatedAt || b.lastUpdatedAt || b.createdAt || 0).getTime();
        return dateB - dateA;
      });
    }
    
    setFilteredProjects(filtered);
  }, [searchTerm, activeTab, projects, user]);

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
  };

  const handleOpenCreateProject = () => {
    setCreateProjectModalOpen(true);
  };

  const handleCloseCreateProject = () => {
    setCreateProjectModalOpen(false);
  };

  const handleProjectCreated = async (projectId: string) => {
    try {
      
      if (!projectId) {
        showError('ID de projeto inválido. O projeto pode ter sido criado, mas não foi possível abri-lo.');
        await fetchProjects(); // Atualizar a lista de projetos
        return;
      }
      
      // Atualizar a lista de projetos antes de navegar
      await fetchProjects();
      
      // Mostrar mensagem de sucesso
      showSuccess('Projeto criado com sucesso! Redirecionando...');
      
      // Pequeno delay para garantir que a UI seja atualizada antes do redirecionamento
      setTimeout(() => {
        // Navegar para o novo projeto
        navigate(`/project/${projectId}`);
      }, 800);
    } catch (error: any) {
      console.error('Erro após criação do projeto:', error);
      showError(`Erro ao processar o projeto criado: ${error.message}`);
      
      // Tentar atualizar a lista mesmo com erro
      try {
        await fetchProjects();
      } catch (e) {
        console.error('Erro adicional ao atualizar lista de projetos:', e);
      }
    }
  };



  // Função para verificar se o usuário é administrador do projeto
  const isUserProjectAdmin = (project: Project) => {
    if (!user || !user.id) {
      return false; // Retorna um booleano explícito
    }
    
    const projectName = project.title || project.name || 'Sem nome';
    const userId = user.id;
    
    // Verificar se o projeto tem ID
    if (!project.id) {
      return false; // Retorna um booleano explícito
    }
    
    // NOVO: Verificar se o usuário é administrador do sistema ou desenvolvedor
    // Administradores do sistema e desenvolvedores podem gerenciar qualquer projeto
    if (isAdminOrDeveloper) {
      return true; // Administradores do sistema podem deletar qualquer projeto
    }

    // 1. Verificar se o usuário é o criador ou proprietário do projeto
    if (project.creatorId === userId || project.ownerId === userId) {
      return true; // Retorna um booleano explícito
    }
    
    // 2. Verificar se o usuário está na lista de administradores (campo 'administrators')
    if (Array.isArray(project.administrators)) {
      const adminMatch = project.administrators.find((admin: ProjectMember) => admin.id === userId);
      if (adminMatch) {
        return true; // Retorna um booleano explícito
      }
    }
    
    // 3. Verificar se o usuário está na lista de administradores (campo 'admins')
    if (Array.isArray(project.admins)) {
      const adminMatch = project.admins.find(admin => admin.id === userId);
      if (adminMatch) {
        return true; // Retorna um booleano explícito
      }
    }
    
    // Se chegou até aqui, o usuário não é administrador
    return false; // Retorna um booleano explícito
  };
  
  // Função para verificar se o usuário pode editar o projeto (criador ou administrador)
  const canEditProject = (project: Project) => {
    if (!user || !user.id) {
      return false;
    }
    
    const projectName = project.title || project.name || 'Sem nome';
    const userId = user.id;
    
    // Verificar se o projeto tem ID
    if (!project.id) {
      return false;
    }
    
    // NOVO: Administradores do sistema e desenvolvedores podem editar qualquer projeto
    if (isAdminOrDeveloper) {
      return true;
    }
    
    // Verificar se o usuário é o criador do projeto
    if (project.creatorId === userId || project.ownerId === userId) {
      return true;
    }
    
    // Verificar se o usuário é administrador
    return isUserProjectAdmin(project);
  };

  const handleDeleteProject = (projectId: string) => {
    
    // Encontrar o projeto pelo ID
    const project = projects.find(p => p.id === projectId);
    if (!project) {
      return;
    }
    
    // Configurar o projeto para exclusão e abrir o modal
    const projectForDelete = {
      id: projectId,
      name: project.title || project.name || 'Projeto sem título'
    };
    
    setProjectToDelete(projectForDelete);
    setDeleteProjectModalOpen(true);
  };
  
  const handleDeleteSuccess = () => {
    // Atualizar a lista de projetos após a exclusão
    fetchProjects();
    showSuccess('Projeto excluído com sucesso!');
  };
  
  const handleCloseDeleteModal = () => {
    setDeleteProjectModalOpen(false);
    setProjectToDelete(null);
  };

  const handleEditProject = (project: Project) => {
    
    setProjectToEdit(project);
    setEditProjectModalOpen(true);
  };

  const handleEditSuccess = () => {
    // Atualizar a lista de projetos após a edição
    fetchProjects();
    showSuccess('Projeto atualizado com sucesso!');
  };

  const handleCloseEditModal = () => {
    setEditProjectModalOpen(false);
    setProjectToEdit(null);
  };

  return (
    <HubContainer>
      <HeaderBar 
        title="Hub de Projetos"
        showBackButton={false}
        subtitle="Gerencie e acompanhe todos os seus projetos em um só lugar"
        actionButton={
          <Box sx={{ display: 'flex', gap: 2 }}>
            {isAdminOrDeveloper ? (
              <Tooltip title="Painel Global de Atividades">
                <Button
                  variant="outlined"
                  color="primary"
                  startIcon={<AssignmentIcon />}
                  onClick={() => navigate('/admin/activities/dashboard')}
                  sx={{
                    borderColor: 'rgba(255, 255, 255, 0.2)',
                    color: '#fff',
                    '&:hover': {
                      borderColor: 'rgba(255, 255, 255, 0.4)',
                      backgroundColor: 'rgba(255, 255, 255, 0.05)'
                    }
                  }}
                >
                  Atividades
                </Button>
              </Tooltip>
            ) : null}
            
            <CreateButton
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleOpenCreateProject}
            >
              Novo Projeto
            </CreateButton>
          </Box>
        }
      />
      
      <Box sx={{ height: '56px' }} />

      <MainContent>
        <SearchContainer>
          <TextField
            fullWidth
            variant="outlined"
            placeholder="Buscar projetos..."
            value={searchTerm}
            onChange={handleSearch}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: 'rgba(255, 255, 255, 0.5)' }} />
                </InputAdornment>
              ),
            }}
            sx={{
              backgroundColor: 'rgba(255, 255, 255, 0.05)',
              borderRadius: '8px',
              '& .MuiOutlinedInput-root': {
                '& fieldset': {
                  borderColor: 'rgba(255, 255, 255, 0.1)',
                },
                '&:hover fieldset': {
                  borderColor: 'rgba(255, 255, 255, 0.2)',
                },
                '&.Mui-focused fieldset': {
                  borderColor: '#2196f3',
                },
              },
              '& .MuiInputBase-input': {
                color: 'white',
              },
            }}
          />
        </SearchContainer>

        <TabsContainer>
          <TabButton
            active={activeTab === 'all'}
            onClick={() => setActiveTab('all')}
          >
            Todos os projetos
          </TabButton>
          <TabButton
            active={activeTab === 'recent'}
            onClick={() => setActiveTab('recent')}
          >
            Recentes
          </TabButton>
          <TabButton
            active={activeTab === 'owned'}
            onClick={() => setActiveTab('owned')}
          >
            Seus projetos
          </TabButton>
        </TabsContainer>

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', my: 8 }}>
            <CircularProgress />
          </Box>
        ) : filteredProjects.length > 0 ? (
          <>
            <ProjectsCountText sx={{ marginBottom: 1 }}>
              {filteredProjects.length} {filteredProjects.length === 1 ? 'projeto encontrado' : 'projetos encontrados'}
            </ProjectsCountText>
            
            <SectionTitle variant="h5" sx={{ marginBottom: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <DashboardIcon />
                Seus Projetos
              </Box>
              {hasScrollOverflow && (
                <Typography 
                  variant="caption" 
                  sx={{ 
                    color: 'rgba(255, 255, 255, 0.6)',
                    display: 'flex',
                    alignItems: 'center',
                    fontSize: '14px'
                  }}
                >
                  <span style={{ marginRight: '4px' }}>Role para ver mais</span>
                  <span className="scroll-indicator">&#8595;</span> {/* Símbolo de seta para baixo com animação */}
                </Typography>
              )}
            </SectionTitle>
            <ProjectsGrid ref={projectsGridRef} hasScroll={hasScrollOverflow}>
              {filteredProjects.map(project => {
                // Verificar se o usuário é administrador deste projeto
                const isAdmin = isUserProjectAdmin(project);
                
                // Verificar se o usuário pode editar este projeto
                const canEdit = canEditProject(project);
                
                // Garantir que isAdmin seja um booleano explícito
                const isAdminBoolean = Boolean(isAdmin);
                
                return (
                  <ProjectCard 
                    key={project.id} 
                    project={project} 
                    isAdmin={isAdminBoolean}
                    onDeleteClick={handleDeleteProject}
                    onEditClick={canEdit ? handleEditProject : undefined}
                  />
                );
              })}
            </ProjectsGrid>
          </>
        ) : (
          <EmptyState>
            <EmptyStateIcon>
              <FolderIcon />
            </EmptyStateIcon>
            <EmptyStateText variant="h5">
              Nenhum projeto encontrado
            </EmptyStateText>
            <EmptyStateSubtext variant="body1">
              {searchTerm 
                ? 'Não encontramos projetos correspondentes à sua pesquisa. Tente outros termos.'
                : 'Comece criando seu primeiro projeto para organizar seu trabalho.'}
            </EmptyStateSubtext>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleOpenCreateProject}
              sx={{
                backgroundColor: '#2196f3',
                color: 'white',
                '&:hover': {
                  backgroundColor: '#1976d2',
                },
              }}
            >
              Criar novo projeto
            </Button>
          </EmptyState>
        )}

        {/* Novo componente de modal para criação de projetos */}
        <CreateProjectModal 
          open={createProjectModalOpen}
          onClose={handleCloseCreateProject}
          onProjectCreated={handleProjectCreated}
          title="Criar novo projeto"
          placeholderName="Nome do projeto"
          placeholderDescription="Descreva brevemente o propósito do projeto (opcional)"
        />

        
        {/* Modal de exclusão de projeto - só renderizado quando há um projeto para excluir */}
        {projectToDelete && deleteProjectModalOpen && (
          <DeleteProjectModal
            open={deleteProjectModalOpen}
            projectId={projectToDelete.id}
            projectName={projectToDelete.name}
            onClose={handleCloseDeleteModal}
            onSuccess={handleDeleteSuccess}
          />
        )}
        
        {/* Modal de edição de projeto - só renderizado quando há um projeto para editar */}
        {projectToEdit && editProjectModalOpen && (
          <EditProjectModal
            open={editProjectModalOpen}
            project={projectToEdit}
            onClose={handleCloseEditModal}
            onSuccess={handleEditSuccess}
          />
        )}
      </MainContent>
    </HubContainer>
  );
};

export default HubPage;